import clsx from 'clsx';
import { forwardRef, useId } from 'react';
import {
  Label as RAC_Label,
  Slider as RAC_Slider,
  SliderOutput as RAC_SliderOutput,
  SliderThumb as RAC_SliderThumb,
  SliderTrack as RAC_SliderTrack
} from 'react-aria-components';
import styles from './Slider.module.css';
import type { SliderProps } from './Slider.types';

const Slider = forwardRef<HTMLDivElement, SliderProps<number | number[]>>(
  ({ label, thumbLabels, minValue = 0, maxValue = 100, renderOutput, outputDisplay = 'chip', ...props }, ref) => {
    // Calculate disabled area percentage from props
    const disabledPercentage = minValue > 0 ? (minValue / maxValue) * 100 : 0;
    const usablePercentage = 100 - disabledPercentage;
    const labelId = useId();

    return (
      <RAC_Slider
        {...props}
        minValue={minValue}
        maxValue={maxValue}
        ref={ref}
        className={styles.slider}
        data-test-id="Slider"
      >
        {label && <RAC_Label id={labelId}>{label}</RAC_Label>}
        <RAC_SliderOutput className={styles.output}>
          {({ state }) => {
            const values = state.values.length === 1 ? state.values[0] : state.values;
            const content = renderOutput
              ? renderOutput(values)
              : state.values.map((_, i) => state.getThumbValueLabel(i)).join(' – ');

            return (
              <div
                className={clsx(styles.outputDisplay, {
                  [styles.chip]: outputDisplay === 'chip'
                })}
              >
                {content}
              </div>
            );
          }}
        </RAC_SliderOutput>
        <div className={styles.trackContainer}>
          {/* Disabled area (outside SliderTrack, blocks interaction) */}
          {disabledPercentage > 0 && (
            <div
              className={styles.disabledTrackArea}
              data-test-id="slider-disabled-area"
              style={{ width: `${disabledPercentage}%` }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
            />
          )}
          {/* RAC SliderTrack (only usable range) */}
          <RAC_SliderTrack className={styles.trackWrapper} style={{ width: `${usablePercentage}%` }}>
            {({ state }) => {
              const isRange = state.values.length > 1;

              // For range mode: fill between thumb 0 and thumb 1
              // For single mode: fill from start to thumb 0
              const fillStart = isRange ? state.getThumbPercent(0) * 100 : 0;
              const fillEnd = isRange ? state.getThumbPercent(1) * 100 : state.getThumbPercent(0) * 100;
              const fillWidth = fillEnd - fillStart;

              return (
                <>
                  {/* Filled area - between thumbs for range, from start for single */}
                  <div
                    className={styles.fill}
                    style={{
                      left: `${fillStart}%`,
                      width: `${fillWidth}%`
                    }}
                    data-range={isRange}
                  />
                  {state.values.map((_, i) => (
                    <RAC_SliderThumb key={i} index={i} aria-label={thumbLabels?.[i]} className={styles.thumb} />
                  ))}
                </>
              );
            }}
          </RAC_SliderTrack>
        </div>
      </RAC_Slider>
    );
  }
);

Slider.displayName = 'Slider';

export { Slider };
